import { useState, useCallback, useEffect, useRef } from "react";

interface UseBarcodeScanner {
  lastScannedCode: string | null;
  isScanning: boolean;
  clearScannedCode: () => void;
}

/**
 * Hook to detect barcode scanner input
 * Barcode scanners work like keyboards - they type very fast and usually end with Enter
 * We detect rapid input (< 50ms between keystrokes) as barcode input
 */
export function useBarcodeScanner(
  onBarcodeScanned?: (barcode: string) => void,
  enabled: boolean = true
): UseBarcodeScanner {
  const [lastScannedCode, setLastScannedCode] = useState<string | null>(null);
  const [isScanning, setIsScanning] = useState(false);
  
  const bufferRef = useRef<string>("");
  const lastKeyTimeRef = useRef<number>(0);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearScannedCode = useCallback(() => {
    setLastScannedCode(null);
  }, []);

  const processBarcode = useCallback((code: string) => {
    // Clean the barcode - remove any whitespace
    const cleanCode = code.trim();
    
    if (cleanCode.length >= 3) { // Minimum barcode length
      setLastScannedCode(cleanCode);
      setIsScanning(false);
      
      if (onBarcodeScanned) {
        onBarcodeScanned(cleanCode);
      }
    }
    
    bufferRef.current = "";
  }, [onBarcodeScanned]);

  useEffect(() => {
    if (!enabled) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore if we're in an input that's not for barcode scanning
      const target = e.target as HTMLElement;
      const isInInput = target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement;
      
      // Allow scanning in inputs that have data-barcode-scanner attribute
      const allowScanning = target.hasAttribute('data-barcode-scanner');
      
      // If in a regular input without the scanner attribute, don't capture
      if (isInInput && !allowScanning) {
        return;
      }

      const currentTime = Date.now();
      const timeDiff = currentTime - lastKeyTimeRef.current;
      lastKeyTimeRef.current = currentTime;

      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // If Enter key, process the barcode
      if (e.key === "Enter") {
        if (bufferRef.current.length >= 3) {
          e.preventDefault();
          processBarcode(bufferRef.current);
        }
        bufferRef.current = "";
        return;
      }

      // Only accept printable characters
      if (e.key.length === 1 && !e.ctrlKey && !e.altKey && !e.metaKey) {
        // If time between keystrokes is > 100ms, it's probably manual typing
        // Reset the buffer for a new potential barcode
        if (timeDiff > 100) {
          bufferRef.current = "";
        }

        bufferRef.current += e.key;
        setIsScanning(bufferRef.current.length >= 3);

        // Set timeout to clear buffer if no more input
        timeoutRef.current = setTimeout(() => {
          // If we have a substantial buffer, process it (scanner might not send Enter)
          if (bufferRef.current.length >= 8) { // Typical barcode length
            processBarcode(bufferRef.current);
          } else {
            bufferRef.current = "";
            setIsScanning(false);
          }
        }, 100);
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [enabled, processBarcode]);

  return {
    lastScannedCode,
    isScanning,
    clearScannedCode,
  };
}
